import React from "react";
import "./ChallengeCard.css";
import WhatsAppButton from "../Button/WhatsAppButton";

const ChallengeCard = ({ challenge, onChallengeClick }) => {
  const {
    id,
    challenge: challengeData,
    challenge_detail,
    challenge_batch,
    total_pay,
    payment_status,
    purchase_status,
  } = challenge;

  // Parse dates
  const startDate = new Date(challenge_batch?.start_date);
  const endDate = new Date(challenge_batch?.end_date);
  const currentDate = new Date();

  // Format dates
  const formattedStartDate = startDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedEndDate = endDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // Calculate challenge status
  const getChallengeStatus = () => {
    if (currentDate < startDate) {
      return { status: "upcoming", label: "Upcoming" };
    } else if (currentDate >= startDate && currentDate <= endDate) {
      return { status: "active", label: "Active" };
    } else {
      return { status: "completed", label: "Completed" };
    }
  };

  const challengeStatus = getChallengeStatus();

  // Calculate progress (simplified - you might want to get actual progress from API)
  const calculateProgress = () => {
    if (challengeStatus.status === "upcoming") return 0;
    if (challengeStatus.status === "completed") return 100;

    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysPassed = Math.ceil(
      (currentDate - startDate) / (1000 * 60 * 60 * 24)
    );
    return Math.min(Math.round((daysPassed / totalDays) * 100), 100);
  };

  const progress = calculateProgress();

  // Handle card click
  const handleCardClick = () => {
    onChallengeClick(challengeData?.slug, id);
  };

  // Get days remaining or elapsed
  const getDaysInfo = () => {
    if (challengeStatus.status === "upcoming") {
      const daysToStart = Math.ceil(
        (startDate - currentDate) / (1000 * 60 * 60 * 24)
      );
      return `Starts in ${daysToStart} day${daysToStart !== 1 ? "s" : ""}`;
    } else if (challengeStatus.status === "active") {
      const daysRemaining = Math.ceil(
        (endDate - currentDate) / (1000 * 60 * 60 * 24)
      );
      return `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining`;
    } else {
      return "Challenge completed";
    }
  };

  return (
    <div className="challenge-card" onClick={handleCardClick}>
      {/* Status Chip */}
      <div className={`challenge-status-chip ${challengeStatus.status}`}>
        {challengeStatus.label}
      </div>

      {/* Challenge Image */}
      <div className="challenge-image-container">
        <img
          src={challengeData?.hero_image || "/path-to-placeholder-image"}
          alt={challengeData?.title}
          className="challenge-image"
        />
        {/* Progress overlay for active challenges */}
        {challengeStatus.status === "active" && (
          <div className="progress-overlay">
            <div className="progress-circle">
              <svg className="progress-ring" width="60" height="60">
                <circle
                  className="progress-ring-background"
                  cx="30"
                  cy="30"
                  r="25"
                />
                <circle
                  className="progress-ring-progress"
                  cx="30"
                  cy="30"
                  r="25"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 25}`,
                    strokeDashoffset: `${2 * Math.PI * 25 * (1 - progress / 100)}`,
                  }}
                />
              </svg>
              <span className="progress-text">{progress}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Challenge Content */}
      <div className="challenge-content">
        <div className="challenge-header">
          <h3 className="challenge-title">{challengeData?.title}</h3>
          <p className="challenge-subtitle">{challengeData?.hero_title}</p>
        </div>

        <div className="challenge-details">
          <div className="challenge-info-row">
            <span className="info-label">Duration:</span>
            <span className="info-value">
              {formattedStartDate} - {formattedEndDate}
            </span>
          </div>

          {challenge_batch?.batch_display_name && (
            <div className="challenge-info-row">
              <span className="info-label">Batch:</span>
              <span className="info-value">
                {challenge_batch.batch_display_name}
              </span>
            </div>
          )}

          <div className="challenge-info-row">
            <span className="info-label">Checkpoints:</span>
            <span className="info-value">
              {challengeData?.total_checkpoints || 0}
            </span>
          </div>

          <div className="challenge-info-row">
            <span className="info-label">Type:</span>
            <span className="info-value capitalize">
              {challengeData?.challenge_type}
            </span>
          </div>
        </div>

        <div className="challenge-status-info">
          <p className="days-info">{getDaysInfo()}</p>
        </div>

        {/* Action Buttons */}
        <div className="challenge-actions">
          <button
            className={`access-button ${challengeStatus.status}`}
            onClick={handleCardClick}
          >
            {challengeStatus.status === "upcoming"
              ? "View Details"
              : challengeStatus.status === "active"
                ? "Continue Challenge"
                : "View Results"}
          </button>

          {/* WhatsApp Group Button */}
          {challenge_batch?.whatsapp_group_url && (
            <div className="whatsapp-button-wrapper">
              <WhatsAppButton groupLink={challenge_batch.whatsapp_group_url} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengeCard;
